import { Timestamp } from 'firebase/firestore';
import moment from 'moment';
import {TimeUtils} from '../utils/Utils';
import {DeliveryType, Identity} from './Identity';

export interface FSConfig {
  data: string;
  venue_id: number;
}

export interface FeaturesConfig {
  use_tele_login?: boolean;
}

export interface VenueBranding {
  theme: string;
  font: string;

  roundness?: string;
  primary_color?: string;
  secondary_color?: string;
  contrast_color?: string;
  surface_color?: string;
  custom_font?: string;
  custom_font_style?: string;
  logo_file?: string;
  logo_badge_file?: string;
  background_file?: string;
  body_color?: string;
  secondary_button_color?: string;
  payment_icon_style?: string;
  notification_color?: string;
  notification2_color?: string;
  divider_color?: string;
  outline_color?: string;
  text_color?: string;
}

export interface LoyaltyConfigProgram {
  id: string;
  active: boolean;
  name: string;
  description?: string;
  magstripe_regex?: string;
  discount_program?: string;
  points_formula?: string;
  auto_link?: boolean;
  self_signup?: boolean;
}

export interface LoyaltyConfig {
  active: boolean;
  active_online?: boolean;
  programs: LoyaltyConfigProgram[];
}

// Item class tree mirrored from the server, e.g. {"food": {"main": null, ...}, ...}.
// Lets the client evaluate ic= menu filters hierarchically (ic=food matches "main").
export interface IcTree {
  [ic: string]: IcTree | null;
}

export interface VenueConfig {
  dt?: ConfigDeliverTypes;
  ic?: IcTree;
  name: string;
  allow_reorder: boolean;
  start_tab?: string;
  deliver_type: string;
  expected_delivery_time: number;
  payment?: ConfigPayment;
  locale?: ConfigLocale;
  show_discount: boolean;
  show_overview: boolean;
  bus_name?: string;
  bus_address?: string;
  public_address?: string;
  email?: string;
  phone?: string;
  org_number?: string;
  swish_info?: string;
  text_db?: {};
  tip?: TipConfig;
  use_translations: boolean;
  kiosk_state?: string;
  features?: FeaturesConfig;
  branding?: VenueBranding;
  giftcard_template?: string;
  loyalty?: LoyaltyConfig;
  voucher?: VoucherConfig;
  review?: ReviewConfig;
  tablebooking?: TableBookingConfig;
}

export interface ConfigDeliverTypes {
  inhouse?: ConfigDeliverType;
  takeaway?: ConfigDeliverType;
  roomservice?: ConfigDeliverType;
  pickup?: ConfigDeliverType;
  foodcourt?: ConfigDeliverType;
}

export interface ConfigDeliverType {
  open: boolean;
  show_time: boolean;
  prep_time?: number;
  title: string;
  desc: string;
  menu_filter_tag?: string;
  table_regex?: string;
  kitchen_time?: ConfigTime[];
  call_waiter?: boolean;
  selectable?: boolean;
  slotting?: {namespaces: {id: string, name: string}[]};
  room_per_table?: boolean;
}

export interface ConfigTime {
  start: number;
  stop: number;
  day: number[];
}

// Derived on the server from the venue's currency and tax jurisdiction (see MoneyUtils.configure).
export interface ConfigLocale {
  currency: string;   // ISO 4217, e.g. "NOK"
  locale: string;     // BCP 47 for number formatting, e.g. "nb-NO"
  country: string;    // ISO 3166-1 alpha-2 merchant country for the wallet buttons, e.g. "NO"
  language?: string;  // the market's language ("sv", "nb"), the default guest language; see TranslateService.venueLanguage
}

export interface ConfigPayment {
  prepaid_id: string;
  prepaid_name: string;
  swish: boolean;
  // Vipps MobilePay (Norway). Set by the server when the venue's Vipps setup is complete; only offered when
  // settling a bill (livesplit / payment link), since the server captures at authorization.
  vipps?: boolean;
  card: boolean;
  viva: ConfigViva;
  google_pay_mode: string;
  bambora_merchant_number?: string;
}

export interface ConfigViva {
  active: boolean;
  merchant_id: string;
}

export interface FSConnectAccount {
  state: string;
  date?: Timestamp;
}

export interface  VoucherConfig {
  vendors?: {id: string, name: string, company_name: string, email: string, phone: string, org_number: string}[];
  collins_voucher_mode?: string;
}

export interface SubReviewsConfig {
  rating_food?: boolean;
  rating_service?: boolean;
  rating_ambiance?: boolean;
  rating_value?: boolean;
}

export const DEFAULT_REVIEW_TEXT = 'Hur var din upplevelse hos oss?';

export interface ReviewConfig {
  enabled: boolean;
  qr_code_enabled?: boolean;
  review_text?: string;
  google_place_id?: string;
  google_min_rating?: number;
  sub_reviews?: SubReviewsConfig;
}

export interface ReviewData {
  rating: number;
  rating_food: number;
  rating_service: number;
  rating_ambiance: number;
  rating_value: number;
  locked?: boolean;
}

export interface TipLevelConfig {
  active?: boolean;
  when?: string;
  interface: "pos" | "payment_link" | "livesplit" | "closed",
  filter?: string;
  tip_mode: "OFF" | "5-10-15" | "5-10-20" | "10-15-20" | "15-20-25";
  force_selection?: boolean;
}

export interface TipConfig {
  tip_levels?: TipLevelConfig[];
}

// Table Booking Config Types

// Separate tables linked by booking_type_id
export interface BookingTypeSeatingDuration {
  booking_type_id: string;
  id: string;
  label: string;
  minutes: number;
  is_default?: boolean;
}

export interface BookingTypeOpenHours {
  booking_type_id: string;
  when: string;  // WHEN expression, e.g. "monday-friday 17:00-22:00"
}

export interface BookingTypeDeviation {
  booking_type_id: string;
  when: string;  // WHEN expression, e.g. "2024-12-24"
  open_closed: string;  // "open" | "closed"
}

export interface SpecialRequestOption {
  id: string;
  label: string;
  type: string;  // "boolean" | "number" | "text"
  icon?: string;
  is_active?: boolean;
}

// BookingType without nested arrays - those are in separate tables
export interface BookingType {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  min_age?: number;
  fixed_price_cents?: number;
  min_party_size?: number;
  max_party_size?: number;
  areas?: string;  // Comma-separated list of areas, e.g. "matsal, ute"
  sort_order?: number;
  is_active?: boolean;
}

export interface TableCombination {
  is_active?: boolean;
  seats: number;
  tables: string;  // Comma-separated list of table names, e.g. "Matsal 1, Matsal 2"
}

export interface WastePolicyThreshold {
  days_ahead: number;  // Days until booking
  max_waste: number;   // Max allowed waste (unused seats)
}

// All settings flattened directly into TableBookingConfig
export interface TableBookingConfig {
  enabled: boolean;
  // Booking types and related tables
  booking_types?: BookingType[];
  booking_types_open_hours?: BookingTypeOpenHours[];
  booking_types_deviations?: BookingTypeDeviation[];
  booking_types_seating_durations?: BookingTypeSeatingDuration[];
  // Table combinations for larger parties
  table_combinations?: TableCombination[];
  // Waste policy for table allocation
  waste_policy_thresholds?: WastePolicyThreshold[];
  combo_penalty?: number;  // Extra "waste" penalty for using table combinations
  turnaround_minutes?: number;  // Time between bookings for cleanup
  // Settings (flattened, not nested)
  max_party_size?: number;
  min_party_size?: number;
  slot_interval_minutes?: number;
  min_advance_hours?: number;
  max_advance_days?: number;
  default_duration_minutes?: number;
  special_request_options?: SpecialRequestOption[];
  auto_confirm?: boolean;
  send_confirmation_email?: boolean;
  send_confirmation_sms?: boolean;
  send_reminder_email?: boolean;
  reminder_hours_before?: number;
  show_campaign_code_field?: boolean;
  terms_url?: string;
  privacy_policy_url?: string;
  // Waitlist (served by samwise)
  waitlist_queue_enabled?: boolean;
  waitlist_guest_queue?: boolean;
  waitlist_notify_enabled?: boolean;
  waitlist_response_minutes?: number;
  waitlist_priority_minutes?: number;
}

export function findPlausibleNonLoggedInDeliverType(cfg: VenueConfig, identity: Identity) {
  if (hasMoreThanOneDeliverType(cfg)) {
    if (identity?.table) {
      if (identity.table.startsWith("Takeaway") && cfg.dt?.takeaway) {
        return DeliveryType.Takeaway;
      }
      if (identity.table.startsWith("Pickup") && cfg.dt?.pickup) {
        return DeliveryType.Pickup;
      }
      if (identity.table.startsWith("Room") && cfg.dt?.roomservice) {
        return DeliveryType.Roomservice;
      }
      if (identity.table.startsWith("Food") && cfg.dt?.foodcourt) {
        return DeliveryType.Foodcourt;
      }
    } else {
      return identity?.deliveryType;
    }
  } else if (cfg.dt) {
    if (cfg.dt.takeaway) {
      return DeliveryType.Takeaway;
    }
    if (cfg.dt.pickup) {
      return DeliveryType.Pickup;
    }
    if (cfg.dt.roomservice) {
      return DeliveryType.Roomservice;
    }
    if (cfg.dt.foodcourt) {
      return DeliveryType.Foodcourt;
    }
  }
  return "inhouse";
}

export function hasMoreThanOneDeliverType(cfg: VenueConfig): boolean {
  const pickup = cfg.dt?.pickup?.selectable ?? (cfg.dt?.pickup != null);
  const t = (cfg.dt?.inhouse ? 1 : 0) + (cfg.dt?.takeaway ? 1 : 0) + (pickup ? 1 : 0) + (cfg.dt?.foodcourt ? 1 : 0);
  return t > 1;
}

export function isKitchenOpen(cfg: VenueConfig): boolean {
  const kitchen_time = cfg?.dt?.inhouse?.kitchen_time;
  if (kitchen_time) {
    const day = moment().isoWeekday() - 1;
    for (const kt of kitchen_time) {
      if (kt.day?.includes(day)) {
        const hourDec = TimeUtils.getCurrentH30();
        return hourDec >= kt.start && hourDec < kt.stop;
      }
    }
  }
  return true;
}
